const template = document.createElement("template");
template.innerHTML = `
<style>
  :host{
    display: block;
    font-family: var(--ctl-font-sans, system-ui, -apple-system, Segoe UI, Roboto, Arial);
  }

  .wrap{
    display: flex;
    flex-direction: column;
    gap: var(--ctl-space-2);
  }

  .header{
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--ctl-space-3);
  }

  .label{
    font-size: var(--ctl-font-size-md);
    font-weight: var(--ctl-font-weight-semibold);
    color: var(--ctl-color-text);
    line-height: 1.4;
  }

  .value{
    font-size: var(--ctl-font-size-sm);
    font-weight: var(--ctl-font-weight-bold);
    color: var(--ctl-color-muted);
    letter-spacing: var(--ctl-tracking-normal);
  }

  .track{
    width: 100%;
    height: var(--track-height, 8px);
    background: color-mix(in srgb, var(--ctl-color-border) 50%, var(--ctl-color-surface));
    border-radius: var(--track-radius, var(--ctl-radius-full));
    overflow: hidden;
    position: relative;
  }

  .bar{
    height: 100%;
    width: var(--progress, 0%);
    background: var(--bar-color);
    border-radius: var(--track-radius, var(--ctl-radius-full));
    transition: width var(--ctl-transition-slow);
    position: relative;
  }

  /* animated shimmer for indeterminate */
  :host([indeterminate]) .bar{
    width: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      var(--bar-color),
      transparent
    );
    background-size: 200% 100%;
    animation: ctlProgressShimmer 1.5s ease-in-out infinite;
  }

  @keyframes ctlProgressShimmer{
    0%{ background-position: 200% 0; }
    100%{ background-position: -200% 0; }
  }

  /* striped variant */
  :host([striped]) .bar{
    background-image: linear-gradient(
      45deg,
      var(--ctl-color-stripe) 25%,
      transparent 25%,
      transparent 50%,
      var(--ctl-color-stripe) 50%,
      var(--ctl-color-stripe) 75%,
      transparent 75%,
      transparent
    );
    background-size: 16px 16px;
  }

  :host([striped][animated]) .bar{
    animation: ctlProgressStripes 1s linear infinite;
  }

  @keyframes ctlProgressStripes{
    0%{ background-position: 0 0; }
    100%{ background-position: 16px 0; }
  }

  /* sizes */
  :host([size="sm"]){
    --track-height: 6px;
    --track-radius: var(--ctl-radius-full);
  }

  :host(:not([size]), [size="md"]){
    --track-height: 8px;
    --track-radius: var(--ctl-radius-full);
  }

  :host([size="lg"]){
    --track-height: 12px;
    --track-radius: var(--ctl-radius-full);
  }

  /* intent -> bar color */
  :host([intent="neutral"]) { --bar-color: var(--ctl-color-muted); }
  :host([intent="info"])    { --bar-color: var(--ctl-color-info); }
  :host([intent="success"]) { --bar-color: var(--ctl-color-success); }
  :host([intent="warning"]) { --bar-color: var(--ctl-color-warning); }
  :host([intent="danger"])  { --bar-color: var(--ctl-color-danger); }
  :host(:not([intent]))     { --bar-color: var(--ctl-color-info); }

  /* hide header if no label/value */
  .header:empty{
    display: none;
  }
</style>

<div class="wrap" part="wrap">
  <div class="header" part="header">
    <div class="label" part="label">
      <slot name="label"></slot>
    </div>
    <div class="value" part="value" id="value"></div>
  </div>
  <div class="track" part="track" role="progressbar" aria-valuemin="0" aria-valuemax="100">
    <div class="bar" part="bar"></div>
  </div>
</div>
`;

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

export class CtlProgressBar extends HTMLElement {
  static get observedAttributes() {
    return ["value", "max", "intent", "size", "striped", "animated", "indeterminate", "show-value"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
    this._track = this.shadowRoot.querySelector(".track");
    this._bar = this.shadowRoot.querySelector(".bar");
    this._valueEl = this.shadowRoot.getElementById("value");
  }

  connectedCallback() {
    // defaults
    if (!this.hasAttribute("intent")) this.setAttribute("intent", "info");
    if (!this.hasAttribute("size")) this.setAttribute("size", "md");
    if (!this.hasAttribute("max")) this.setAttribute("max", "100");
    if (!this.hasAttribute("value")) this.setAttribute("value", "0");
    
    this._sync();
  }

  attributeChangedCallback() {
    this._sync();
  }

  // attrs
  get value() { 
    const v = parseFloat(this.getAttribute("value"));
    return isNaN(v) ? 0 : v;
  }
  set value(v) { 
    const n = parseFloat(v);
    this.setAttribute("value", isNaN(n) ? "0" : String(n)); 
  }

  get max() { 
    const v = parseFloat(this.getAttribute("max"));
    return isNaN(v) || v <= 0 ? 100 : v;
  }
  set max(v) { 
    const n = parseFloat(v);
    this.setAttribute("max", isNaN(n) || n <= 0 ? "100" : String(n)); 
  }

  get intent() { return this.getAttribute("intent") || "info"; }
  set intent(v) { this.setAttribute("intent", String(v || "info")); }

  get size() { return this.getAttribute("size") || "md"; }
  set size(v) { this.setAttribute("size", String(v || "md")); }

  get striped() { return this.hasAttribute("striped"); }
  set striped(v) { v ? this.setAttribute("striped", "") : this.removeAttribute("striped"); }

  get animated() { return this.hasAttribute("animated"); }
  set animated(v) { v ? this.setAttribute("animated", "") : this.removeAttribute("animated"); }

  get indeterminate() { return this.hasAttribute("indeterminate"); }
  set indeterminate(v) { v ? this.setAttribute("indeterminate", "") : this.removeAttribute("indeterminate"); }

  get showValue() { return this.hasAttribute("show-value"); }
  set showValue(v) { v ? this.setAttribute("show-value", "") : this.removeAttribute("show-value"); }

  _sync() {
    // normalize intent
    const intent = this.intent;
    if (!["neutral", "info", "success", "warning", "danger"].includes(intent)) {
      this.setAttribute("intent", "info");
    }

    // normalize size
    const size = this.size;
    if (!["sm", "md", "lg"].includes(size)) {
      this.setAttribute("size", "md");
    }

    const val = this.value;
    const max = this.max;
    const percent = clamp((val / max) * 100, 0, 100);

    // update bar width
    this.style.setProperty("--progress", percent + "%");

    // update aria
    this._track.setAttribute("aria-valuemin", "0");
    this._track.setAttribute("aria-valuenow", String(val));
    this._track.setAttribute("aria-valuemax", String(max));

    if (this.indeterminate) {
      this._track.removeAttribute("aria-valuenow");
      this._track.removeAttribute("aria-valuemax");
      this._track.setAttribute("aria-valuetext", "Loading");
    }

    // show value if requested
    if (this.showValue && !this.indeterminate) {
      this._valueEl.textContent = Math.round(percent) + "%";
      this._valueEl.style.display = "block";
    } else {
      this._valueEl.textContent = "";
      this._valueEl.style.display = "none";
    }
  }
}

customElements.define("ctl-progress-bar", CtlProgressBar);
